/**
 * Combat — data-driven melee between formations, morale, and building attacks.
 *
 * Model (formation-level aggregation, honest to the data):
 * - Damage per soldier per hit = max(1, damage - defender.melee_armour),
 *   applied every attack_interval seconds. hp / morale / move_speed / costs
 *   all come from units_*.json.
 * - Morale shock per casualty plus slow drain in melee; below 25% of max the
 *   formation routs (flees, cannot attack) until morale recovers past 60%.
 * - Infantry deal BUILDING_DAMAGE_FACTOR of their damage to buildings —
 *   prototype tuning (documented): the data has no siege model yet.
 *
 * Mirrors the UE combat/morale subsystem; visuals stay in render/units.ts.
 */
import type { UnitDef } from "../data/types";
import type { FormationView } from "../render/units";
import type { PlacedBuilding } from "./buildings";

export type Team = 0 | 1;

/** Prototype tuning: infantry are bad at demolishing stone. */
export const BUILDING_DAMAGE_FACTOR = 0.2;
/** Morale lost per friendly casualty. */
export const MORALE_PER_DEATH = 3;
/** Morale drained per second while in melee. */
export const MELEE_MORALE_DRAIN = 1.2;
/** Morale regained per second after 4s out of combat. */
export const MORALE_REGEN = 5;
/**
 * PROTOTYPE TUNING: morale drained per second at full upkeep shortfall.
 * Lives here (not organization.ts) to avoid a sim/combat <->
 * sim/organization import cycle; organization.ts imports it from here.
 */
export const UNPAID_MORALE_DRAIN = 3;

export interface Combatant {
  kind: "fighter";
  view: FormationView;
  unit: UnitDef;
  team: Team;
  alive: number;
  /** Bodies at muster; the health-bar denominator (alive only decreases). */
  maxAlive: number;
  morale: number;
  maxMorale: number;
  routing: boolean;
  /** Seconds until the next attack can land. */
  cooldown: number;
  /** Tech-granted additive combat bonuses. */
  damageBonus: number;
  armourBonus: number;
  /** Fractional damage carried between ticks. */
  damagePool: number;
  /** Sim time of last combat involvement (for morale regen). */
  lastCombatT: number;
  order: { target: Combatant | BuildingTarget } | null;
  /**
   * Organization tier (sim/organization.ts). paperStrength drives upkeep
   * and population ("the state pays by the books"); alive/fieldBodies
   * drive combat ("it fields what it can muster").
   */
  tierId: string;
  paperStrength: number;
  /** Accumulated unpaid upkeep (food); sustained arrears cause desertion. */
  supplyDebt: number;
  /** Fractional deserters carried between ticks. */
  desertFrac: number;
  /** True once the formation has fully deserted (earns no strategic point). */
  deserted: boolean;
  /** True while the faction's stockpile is not covering this formation. */
  unpaid: boolean;
}

export interface BuildingTarget {
  kind: "building";
  building: PlacedBuilding;
  team: Team;
}

export function statNum(unit: UnitDef, key: string, fallback: number): number {
  const v = unit.extra[key];
  return typeof v === "number" ? v : fallback;
}

export function makeCombatant(
  view: FormationView,
  unit: UnitDef,
  team: Team,
  count: number,
  mods: { damageBonus?: number; armourBonus?: number } = {},
  org: { tierId?: string; paperStrength?: number } = {},
): Combatant {
  const maxMorale = statNum(unit, "morale", 50);
  return {
    kind: "fighter",
    view,
    unit,
    team,
    alive: count,
    maxAlive: count,
    morale: maxMorale,
    maxMorale,
    routing: false,
    cooldown: 0,
    damageBonus: mods.damageBonus ?? 0,
    armourBonus: mods.armourBonus ?? 0,
    damagePool: 0,
    lastCombatT: -100,
    order: null,
    tierId: org.tierId ?? "",
    paperStrength: org.paperStrength ?? count,
    supplyDebt: 0,
    desertFrac: 0,
    deserted: false,
    unpaid: false,
  };
}

function dist2D(
  a: { x: number; z: number },
  b: { x: number; z: number },
): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

export function inMeleeRange(a: Combatant, b: Combatant): boolean {
  const reach =
    statNum(a.unit, "attack_range", 1.6) + a.view.radius + b.view.radius;
  return dist2D(a.view.anchor, b.view.anchor) <= reach;
}

export function inBuildingRange(a: Combatant, t: BuildingTarget): boolean {
  const [w, d] = footprintOfBuilding(t.building);
  const reach =
    statNum(a.unit, "attack_range", 1.6) + a.view.radius + Math.max(w, d) / 2;
  return dist2D(a.view.anchor, { x: t.building.x, z: t.building.z }) <= reach;
}

function footprintOfBuilding(b: PlacedBuilding): [number, number] {
  const f = b.def.extra["footprint"] as [number, number] | undefined;
  return Array.isArray(f) && f.length === 2 ? [f[0], f[1]] : [4, 4];
}

/**
 * One attacker damages one defender for dt seconds. Returns kills inflicted.
 * Must only be called when in range and the attacker's cooldown has elapsed;
 * the caller owns cooldown bookkeeping so multi-target splitting stays sane.
 */
export function dealMeleeDamage(
  att: Combatant,
  def: Combatant,
  dt: number,
  simNow: number,
): number {
  const raw = statNum(att.unit, "damage", 8) + att.damageBonus;
  const armour = statNum(def.unit, "melee_armour", 0) + def.armourBonus;
  const perHit = Math.max(1, raw - armour);
  const interval = statNum(att.unit, "attack_interval", 1.5);
  const total = perHit * att.alive * (dt / interval);
  const hp = statNum(def.unit, "hp", 100);
  def.damagePool += total;
  const kills = Math.min(def.alive, Math.floor(def.damagePool / hp));
  def.damagePool -= kills * hp;
  if (kills > 0 || total > 0) {
    def.lastCombatT = simNow;
    att.lastCombatT = simNow;
  }
  if (kills > 0) {
    def.alive -= kills;
    applyMoraleShock(def, kills);
  }
  return kills;
}

export function dealBuildingDamage(
  att: Combatant,
  t: BuildingTarget,
  dt: number,
): void {
  const raw = statNum(att.unit, "damage", 8) + att.damageBonus;
  const interval = statNum(att.unit, "attack_interval", 1.5);
  t.building.hp -= raw * BUILDING_DAMAGE_FACTOR * att.alive * (dt / interval);
}

/** Morale: shock from casualties, drain in melee, regen out of combat. */
export function applyMoraleShock(c: Combatant, kills: number): void {
  c.morale = Math.max(0, c.morale - kills * MORALE_PER_DEATH);
  updateRout(c);
}

export function tickMorale(
  c: Combatant,
  dt: number,
  simNow: number,
  inMelee: boolean,
): void {
  if (inMelee) {
    c.morale = Math.max(0, c.morale - MELEE_MORALE_DRAIN * dt);
  } else if (simNow - c.lastCombatT > 4) {
    c.morale = Math.min(c.maxMorale, c.morale + MORALE_REGEN * dt);
  }
  updateRout(c);
}

function updateRout(c: Combatant): void {
  if (!c.routing && c.morale <= c.maxMorale * 0.25) {
    c.routing = true;
    c.order = null;
  } else if (c.routing && c.morale >= c.maxMorale * 0.6) {
    c.routing = false;
  }
}

export function applyUnpaidMorale(
  c: Combatant,
  dt: number,
  strain: number,
): void {
  c.morale = Math.max(
    0,
    c.morale - UNPAID_MORALE_DRAIN * Math.min(1, Math.max(0, strain)) * dt,
  );
  updateRout(c);
}
